// 구독 문의 다이얼로그 — kind=enterprise(도입 문의, 기본) | trial_extension(체험 연장 신청)
// 연장 신청은 체험 종료 후 읽기 전용·잠금 상태의 회사관리자가 운영자에게 기간 부여를 요청하는 경로.

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getApiError } from "@/services/api";
import { billingService } from "@/services/billingService";
import type { InquiryKind } from "@/types/billing";

interface Props {
  planId?: string | null;
  planName?: string;
  kind?: InquiryKind;
  trigger: React.ReactNode;
}

const COPY: Record<InquiryKind, { title: string; placeholder: string; submit: string; done: string }> = {
  enterprise: {
    title: "도입 문의",
    placeholder: "도입 규모·요구사항을 적어주세요.",
    submit: "문의 보내기",
    done: "문의가 접수되었습니다. 담당자가 연락드리겠습니다.",
  },
  trial_extension: {
    title: "이용 기간 연장 신청",
    placeholder: "연장을 원하시는 기간이나 도입 검토 상황을 적어주세요.",
    submit: "연장 신청",
    done: "연장 신청이 접수되었습니다. 확인 후 기간이 부여되면 알림으로 안내드립니다.",
  },
};

export default function EnterpriseInquiryDialog({ planId, planName, kind = "enterprise", trigger }: Props) {
  const { toast } = useToast();
  const copy = COPY[kind];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contact_name: "", contact_phone: "", contact_email: "", message: "" });

  const mutation = useMutation({
    mutationFn: () => billingService.createInquiry({ plan_id: planId, kind, ...form }),
    onSuccess: () => {
      toast({ title: copy.done });
      setOpen(false);
      setForm({ contact_name: "", contact_phone: "", contact_email: "", message: "" });
    },
    onError: (e) => toast({ title: getApiError(e).message, variant: "destructive" }),
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}{planName ? ` · ${planName}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>담당자명</Label>
            <Input value={form.contact_name} onChange={set("contact_name")} placeholder="홍길동" />
          </div>
          <div className="space-y-1">
            <Label>연락처</Label>
            <Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="010-0000-0000" />
          </div>
          <div className="space-y-1">
            <Label>이메일</Label>
            <Input value={form.contact_email} onChange={set("contact_email")} placeholder="name@company.com" />
          </div>
          <div className="space-y-1">
            <Label>{kind === "trial_extension" ? "요청 내용" : "문의 내용"}</Label>
            <Textarea value={form.message} onChange={set("message")} rows={4} placeholder={copy.placeholder} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {copy.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
